import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, ChevronRight, PenLine, Package, Wrench, ShoppingCart } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import * as api from '../../api';
import { TerrainProfileButton } from './TerrainComptages';

// ─── Tuile terrain ────────────────────────────────────────────────────────────
function Tile({ icon: Icon, title, subtitle, badge, onClick, disabled }) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      data-testid={`terrain-tile-${title.toLowerCase().replace(/\s/g, '-')}`}
      className="w-full flex items-center gap-4 p-5 rounded-2xl border text-left transition-all"
      style={{
        background: disabled ? '#F9FAFB' : 'white',
        borderColor: disabled ? '#E5E7EB' : '#D1D5DB',
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        boxShadow: disabled ? 'none' : '0 1px 4px rgba(0,0,0,0.06)',
      }}>
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
        style={{ background: disabled ? '#F3F4F6' : 'var(--primary-50, #EFF6FF)' }}>
        <Icon size={26} style={{ color: disabled ? '#9CA3AF' : 'var(--primary-solid)' }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-base font-bold" style={{  color: disabled ? '#9CA3AF' : '#111827' }}>
          {title}
        </p>
        <p className="text-sm mt-0.5" style={{ color: '#6B7280' }}>{subtitle}</p>
      </div>
      {disabled
        ? <span className="text-xs font-semibold px-2 py-1 rounded-full flex-shrink-0"
            style={{ background: '#F3F4F6', color: '#9CA3AF' }}>À venir</span>
        : badge !== undefined
          ? <span className="text-sm font-bold flex-shrink-0 px-2.5 py-1 rounded-full"
              style={{ background: 'var(--primary-50, #EFF6FF)', color: 'var(--primary-solid)' }}>
              {badge}
            </span>
          : <ChevronRight size={18} style={{ color: '#9CA3AF', flexShrink: 0 }} />
      }
    </button>
  );
}

// ─── Page d'accueil terrain ────────────────────────────────────────────────────
export default function TerrainHome() {
  const navigate = useNavigate();
  const { user, menuRights } = useAuth();
  const [sessionCount, setSessionCount] = useState(null);

  const isAdmin = user?.role === 'admin_org' || user?.role === 'super_admin';
  const hasRight = (key) => isAdmin || (menuRights?.includes(key) ?? false);

  useEffect(() => {
    api.getComptages()
      .then(r => setSessionCount((r.data || []).filter(s => s.statut === 'en_cours').length))
      .catch(() => setSessionCount(0));
  }, []);

  const firstName = user?.name?.split(' ')[0] || 'Opérateur';

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#F9FAFB' }}>
      {/* Header avec profil discret */}
      <div className="flex items-center justify-end px-5 pt-5">
        <TerrainProfileButton />
      </div>

      {/* Logo + Accueil */}
      <div className="flex flex-col items-center justify-center pt-10 pb-8 px-6">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 shadow-sm"
          style={{ background: '#1A3A6E' }}>
          <svg viewBox="0 0 32 32" width="36" height="36">
            <g transform="translate(6, 6) scale(0.833)" stroke="white" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" fill="none">
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
            </g>
          </svg>
        </div>
        <h1 className="text-2xl font-bold" style={{  color: '#111827' }}>Bedee</h1>
        <p className="text-base mt-1" style={{ color: '#6B7280' }}>Bonjour, {firstName}</p>
      </div>

      {/* Tuiles */}
      <div className="flex-1 px-5 space-y-3 pb-8">
        <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: '#9CA3AF' }}>
          Modules disponibles
        </p>

        {/* Comptages — droit terrain_comptages requis */}
        {hasRight('terrain_comptages') && (
        <Tile
          icon={ClipboardList}
          title="Comptages"
          subtitle="Réaliser une session de comptage de matériels"
          badge={sessionCount}
          onClick={() => navigate('/terrain/comptages')}
        />
        )}

        {/* Mes interventions — droit terrain_atelier requis + employe_id */}
        {hasRight('terrain_atelier') && user?.employe_id && (
          <Tile
            icon={Wrench}
            title="Mes interventions"
            subtitle="Consulter et pointer mes ordres de réparation"
            onClick={() => navigate('/terrain/atelier')}
          />
        )}

        {/* Contrats à signer — droit terrain_contrats_location requis */}
        {hasRight('terrain_contrats_location') && (
          <Tile
            icon={PenLine}
            title="Contrats à signer"
            subtitle="Consulter et signer les contrats de location en attente"
            onClick={() => navigate('/terrain/signatures')}
          />
        )}

        {/* Bons de livraison — droit terrain_bons_livraison requis */}
        {hasRight('terrain_bons_livraison') && (
          <Tile
            icon={Package}
            title="Bons de livraison/retour de location"
            subtitle="Réaliser les contrôles et signer les bons de livraison et de retour"
            onClick={() => navigate('/terrain/bons-livraison')}
          />
        )}

        {/* Commandes de vente — droit terrain_commandes_vente requis */}
        {hasRight('terrain_commandes_vente') && (
          <Tile
            icon={ShoppingCart}
            title="Commandes de vente"
            subtitle="Signer les devis et confirmer les commandes"
            onClick={() => navigate('/terrain/commandes-vente')}
          />
        )}
      </div>
    </div>
  );
}
